import convertPayloadToIntegration from './convertPayloadToIntegration';
import { canSave, getMaxStep, getTotalStep } from './builderReducerUtil';

import {
  IB_FETCH_PLAN_SUCCESS,
  IB_TRIGGER_CHANGED,
  IB_FORM_CHANGED,
  IB_ACTION_CHANGED,
  IB_LOCATION_CHANGED,
  IB_AGENT_CHANGED,
  IB_SAVE_NAME,
  IB_APPEND_URL_CHANGED,
  IB_ENDPOINT_CHANGED,
  IB_INTEGRATION_SERVICE_CHANGED,
  IB_INTEGRATION_SERVICE_SELECTOR_CHANGED,
  IB_SCRIPT_CHANGED,
  IB_SAVE_INTEGRATION_START,
  IB_SAVE_INTEGRATION_SUCCESS,
  IB_SAVE_INTEGRATION_FAILURE,
  IB_RESET,
  IB_INTEGRATION_SELECTED,
} from '../../actions/integrationBuilder';

import { ENDPOINT_DELETE_SUCCESS } from '../../actions/endpointsModal';

export const INITIAL_STATE = {
  currentStep: 1,
  totalSteps: null,
  canSave: false,
  integration: {
    id: null,
    plan: {
      id: null,
    },
    form: {
      id: null,
    },
    name: null,
    operation: null,
    type: 'OUTBOUND_WEBHOOK',
    triggeredBy: null,
    endpoint: {
      id: null,
    },
    deployed: true,
    environment: null,
    script: null,
    agents: [],
    integrationService: null,
    integrationServiceSelector: null,
    appendToUrl: null,
    createdBy: 'IB',
  },
  lastSaved: {},
};

function findAgentIndex(integration, agent) {
  if (typeof integration.agents === 'undefined') {
    return -1;
  }
  return integration.agents.findIndex(r => r.id === agent);
}

function getUpdateStepState(state, integration) {
  const maxStep = getMaxStep(integration, state.currentStep);
  return {
    currentStep: maxStep,
    canSave: canSave(integration, state.lastSaved, maxStep, state.totalSteps),
  };
}

export default function builderReducer(state = INITIAL_STATE, action) {
  switch (action.type) {
    case IB_SAVE_INTEGRATION_START: {
      return {
        ...state,
        canSave: false,
      };
    }
    case IB_SAVE_INTEGRATION_FAILURE: {
      return {
        ...state,
        canSave: true,
      };
    }
    case IB_SAVE_INTEGRATION_SUCCESS:
    case IB_INTEGRATION_SELECTED: {
      const integration = convertPayloadToIntegration(
        { ...INITIAL_STATE.integration },
        action.payload,
      );

      const lastSaved = convertPayloadToIntegration(
        { ...INITIAL_STATE.integration },
        action.payload,
      );
      const totalSteps = integration.operation === 'RUN_SCRIPT' ? 4 : 3;
      const maxStep = getMaxStep(integration, 1);

      return {
        ...state,
        integration,
        lastSaved,
        currentStep: maxStep,
        totalSteps,
        canSave: canSave(integration, lastSaved, maxStep, totalSteps),
      };
    }
    case IB_FETCH_PLAN_SUCCESS: {
      const integration = {
        ...state.integration,
        plan: {
          id: action.payload.id,
        },
      };
      return { ...state, integration };
    }
    case IB_FORM_CHANGED: {
      const integration = {
        ...state.integration,
        form: {
          id: action.form,
        },
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_TRIGGER_CHANGED: {
      const integration = {
        ...state.integration,
        triggeredBy: action.trigger,
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_ACTION_CHANGED: {
      const integration = {
        ...state.integration,
        operation: action.action,
      };

      if (!action.hasAgent) {
        integration.environment = 'HOSTED';
      }

      return {
        ...state,
        integration,
        totalSteps: getTotalStep(integration),
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_LOCATION_CHANGED: {
      const integration = {
        ...state.integration,
        environment: action.location,
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_SAVE_NAME: {
      const integration = {
        ...state.integration,
        name: action.name,
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_AGENT_CHANGED: {
      const agents = [...state.integration.agents];
      const index = findAgentIndex(state.integration, action.agent.id);

      if (action.selected && index < 0) {
        // Adding and not already present:
        agents.push(action.agent);
      } else if (!action.selected && index >= 0) {
        // Removing and present:
        agents.splice(index, 1);
      }
      const integration = {
        ...state.integration,
        agents,
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_APPEND_URL_CHANGED: {
      const integration = {
        ...state.integration,
        appendToUrl: action.url,
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_ENDPOINT_CHANGED: {
      const integration = {
        ...state.integration,
        endpoint: {
          id: action.endpoint,
        },
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_INTEGRATION_SERVICE_CHANGED: {
      const integration = {
        ...state.integration,
        integrationService: action.service,
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_INTEGRATION_SERVICE_SELECTOR_CHANGED: {
      const integration = {
        ...state.integration,
        integrationServiceSelector: action.selector,
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_SCRIPT_CHANGED: {
      const integration = {
        ...state.integration,
        script: action.payload,
      };

      return {
        ...state,
        integration,
        ...getUpdateStepState(state, integration),
      };
    }
    case IB_RESET: {
      return INITIAL_STATE;
    }
    case ENDPOINT_DELETE_SUCCESS:
      return {
        ...state,
        integration: {
          ...state.integration,
          endpoint:
            state.integration.endpoint.id === action.payload.id
              ? { id: null }
              : state.integration.endpoint,
        },
      };
    default:
      return state;
  }
}
